/**
 * Format a date string into a human-readable format
 * @param {string} dateString - Date string in ISO format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
import { CURRENCY_CODE, MIN_FRACTION_DIGITS } from '../constants/currencyConfig';

export const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Default formatting options
      const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options
      };
      
      return date.toLocaleDateString(undefined, defaultOptions);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date error';
    }
  };
  
  /**
   * Format a currency amount with the specified currency code
   * @param {number|string} amount - The amount to format
   * @param {string} currencyCode - The currency code (e.g., 'FCFA', 'USD')
   * @param {Object} options - Additional formatting options
   * @returns {string} - Formatted currency string
   */
export const formatCurrency = (amount, currencyCode = CURRENCY_CODE, options = {}) => {
  if (amount === null || amount === undefined) return 'N/A';

  // Try to parse the amount if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Check if parsing resulted in valid number
  if (isNaN(numericAmount)) {
    return 'Invalid amount';
  }

  // Map FCFA to XAF for formatting, but display FCFA as the symbol
  let displayCurrency = currencyCode;
  let formatCurrencyCode = currencyCode;
  if (currencyCode === 'FCFA') {
    formatCurrencyCode = 'XAF';
    displayCurrency = 'FCFA';
  }

  try {
    // Use Intl.NumberFormat for ISO currencies, fallback to custom for FCFA
    if (formatCurrencyCode === 'XAF' || formatCurrencyCode === 'XOF' || formatCurrencyCode === 'USD' || formatCurrencyCode === 'EUR') {
      const formatted = numericAmount.toLocaleString(undefined, {
        style: 'currency',
        currency: formatCurrencyCode,
        minimumFractionDigits: MIN_FRACTION_DIGITS,
        maximumFractionDigits: MIN_FRACTION_DIGITS,
        ...options
      });
      // If the original code was FCFA, replace the currency symbol with FCFA
      if (currencyCode === 'FCFA') {
        // Replace the symbol or code in the formatted string with 'FCFA'
        return formatted.replace(/(XAF|XOF|F\s?CFA|CFA|Fr|\u20A3)/gi, 'FCFA');
      }
      return formatted;
    }

    // Fallback: just show with thousand separators and append currency code
    const formatted = numericAmount.toLocaleString(undefined, {
      minimumFractionDigits: MIN_FRACTION_DIGITS,
      maximumFractionDigits: MIN_FRACTION_DIGITS,
      ...options
    });
    return `${formatted} ${displayCurrency}`;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${numericAmount} ${displayCurrency}`;
  }
};
  
  /**
   * Truncate a string to a specified length with ellipsis
   * @param {string} text - The text to truncate
   * @param {number} maxLength - Maximum length before truncation
   * @returns {string} - Truncated text with ellipsis if needed
   */
  export const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    
    return text.substring(0, maxLength) + '...';
  };
  
  /**
   * Format file size in bytes to human-readable format
   * @param {number} bytes - File size in bytes
   * @param {number} decimals - Number of decimal places
   * @returns {string} - Formatted file size with unit
   */
  export const formatFileSize = (bytes, decimals = 1) => {
    if (bytes === 0) return '0 Bytes';
    if (!bytes) return 'N/A';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };
  
  /**
   * Escape HTML special characters to prevent XSS attacks
   * @param {string} str - The string to escape
   * @returns {string} - The escaped string
   */
  export const escapeHtml = (str) => {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };